import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Ecwid.css";
import { getCategories, getProducts } from "./services/EcwidService";
import { useCart } from "./context/CartContext";

const Ecwid = ({ sidebarOpen, toggleSidebar }) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    setLoading(true);

    const fetchProducts = async () => {
      try {
        if (selectedCategoryId) {
          const items = await getProducts(selectedCategoryId, source.token);
          setProducts(items);
        } else {
          const cats = await getCategories();
          setCategories(cats);

          const results = await Promise.all(
            cats.map((cat) =>
              getProducts(cat.id, source.token).then((products) => ({
                category: cat,
                products,
              }))
            )
          );

          const grouped = results.reduce((acc, { category, products }) => {
            acc[category.id] = { ...category, products };
            return acc;
          }, {});

          setProductsByCategory(grouped);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Error fetching products:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    return () => source.cancel();
  }, [selectedCategoryId]);

  // Render single product card
  const ProductCard = ({ product, showButton = false }) => (
    <div className="product-card" key={product.id}>
      <img
        src={product.imageUrl || "https://via.placeholder.com/150"}
        alt={product.name}
        className="product-image"
      />
      <h3 className="product-name">{product.name}</h3>
      {showButton && (
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="product-price">${product.price}</p>
          <button
            className="bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white py-2 px-3 rounded-md shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl"
            onClick={() => addToCart(product)}
          >
            Add TO Cart
          </button>
        </div>
      )}
    </div>
  );

  // Render all category blocks (when no category is selected)
  const renderAllCategories = () =>
    Object.values(productsByCategory)
      .filter(
        (cat) =>
          cat.products.length &&
          cat.products.some((p) => p.imageUrl) &&
          ![
            "Wedding Bridal Services",
            "Drinks",
            "Repair Services",
            "Test",
          ].includes(cat.name)
      )
      .map((cat) => (
        <div className="amazon-card" key={cat.id}>
          <h3 className="amazon-card-title">{cat.name}</h3>
          <div className="amazon-card-products">
            {cat.products.slice(0, 4).map((p) => (
              <ProductCard product={p} key={p.id} />
            ))}
          </div>
          <div className="text-left mt-2">
            <button
              className="text-blue-600 hover:underline"
              onClick={() => {
                setSelectedCategoryId(cat.id);
                toggleSidebar();
              }}
            >
              See more
            </button>
          </div>
        </div>
      ));

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-cover bg-center products-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 md:py-10">
          <div className="text-center lg:text-left pt-10">
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-2 md:mb-6">
              Discover Our Latest <br />
              <span className="text-dark text-2xl sm:text-7xl">
                Product Collections
              </span>
            </h1>
            <p className="text-md md:text-lg text-black mb-6">
              Shop the best quality products at unbeatable prices. New arrivals
              every week!
            </p>
          </div>
        </div>

        {/* Layout Section */}
        <div className="ecwid-layout px-0 md:px-5 pb-10">
          {/* Sidebar */}
          <aside
            className={
              sidebarOpen ? "ecwid-sidebar openSidebar" : "ecwid-sidebar"
            }
          >
            <h3 className="text-xl mb-4 text-gray-900 font-bold">
              📂 Categories
            </h3>
            <ul>
              <li
                onClick={() => {
                  setSelectedCategoryId(null);
                  toggleSidebar();
                }}
                className={!selectedCategoryId ? "active" : ""}
              >
                All Products
              </li>
              {categories.map((cat) => (
                <li
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategoryId(cat.id);
                    toggleSidebar();
                  }}
                  className={selectedCategoryId === cat.id ? "active" : ""}
                >
                  {cat.name}
                </li>
              ))}
            </ul>
          </aside>

          {/* Main Content */}
          <main className="ecwid-main">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
              </div>
            ) : selectedCategoryId ? (
              <div className="product-grid">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} showButton />
                ))}
              </div>
            ) : (
              <div className="amazon-style-grid">{renderAllCategories()}</div>
            )}
          </main>
        </div>
      </section>
    </div>
  );
};

export default Ecwid;
